/**
 * Tool form component with Vuexy styling.
 * Provides form for creating and editing tools with organized sections.
 */

// React Imports
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// MUI Imports
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

// Component Imports
import { TextField } from '@core/components/mui';
import { CodeEditor } from '../common/CodeEditor';
import { toolService } from '../../services/toolService';
import { useNotificationStore } from '../../store/notificationStore';

// Types
import { ToolExecutionLocation, type ToolDetailDto, type ToolRiskLevel } from '../../types/tool';

const riskLevelToString = (risk: ToolRiskLevel): string => {
  switch (risk) {
    case 0:
      return 'Low';
    case 1:
      return 'Medium';
    case 2:
      return 'High';
    case 3:
      return 'Critical';
    default:
      return 'Low';
  }
};

/**
 * Tool form component for creating and editing tools.
 */
export const ToolForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [_tool, setTool] = useState<ToolDetailDto | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [riskLevel, setRiskLevel] = useState<string>('Low');
  const [executionLocation, setExecutionLocation] = useState<ToolExecutionLocation>(ToolExecutionLocation.Server);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [version, setVersion] = useState('1.0.0');
  const [documentationUrl, setDocumentationUrl] = useState('');
  const [implementationType, setImplementationType] = useState<string>('CSharp');
  const [inputSchemaJson, setInputSchemaJson] = useState<string>('{}');
  const [outputSchemaJson, setOutputSchemaJson] = useState<string>('{}');
  const [implementationJson, setImplementationJson] = useState<string>('{}');
  const [exampleUsageJson, setExampleUsageJson] = useState<string>('{}');
  const [metadataJson, setMetadataJson] = useState<string>('{}');

  const title = useMemo(() => (isEdit ? 'Edit Tool' : 'Create Tool'), [isEdit]);

  useEffect(() => {
    if (isEdit && id) {
      loadTool();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadTool = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await toolService.getTool(id);
      setTool(data);
      setName(data.name);
      setDisplayName(data.displayName);
      setDescription(data.description || '');
      setCategory(data.category || '');
      setRiskLevel(riskLevelToString(data.riskLevel));
      setExecutionLocation(
        data.executionLocation === ToolExecutionLocation.Client || data.executionLocation === ToolExecutionLocation.Both
          ? data.executionLocation
          : ToolExecutionLocation.Server
      );
      setRequiresApproval(!!data.requiresApproval);
      setIsDefault(!!data.isDefault);
      setIsActive(!!data.isActive);
      setVersion(data.version || '1.0.0');
      setDocumentationUrl(data.documentationUrl || '');
      setImplementationType(data.implementationType || 'CSharp');
      setInputSchemaJson(data.inputSchemaJson || '{}');
      setOutputSchemaJson(data.outputSchemaJson || '{}');
      setImplementationJson(data.implementationJson || '{}');
      setExampleUsageJson(data.exampleUsageJson || '{}');
      setMetadataJson(data.metadataJson || '{}');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load tool';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate name from displayName for new tools (basic, safe slug-style)
  useEffect(() => {
    if (!isEdit && !nameTouched) {
      const generated = displayName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '.')
        .replace(/^\.+|\.+$/g, '');
      if (generated !== name) setName(generated);
    }
  }, [displayName, isEdit, name, nameTouched]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (!category.trim()) {
      setError('Category is required');
      return;
    }

    if (!isEdit && !name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        await toolService.updateTool(id, {
          displayName: displayName.trim(),
          description: description.trim(),
          category: category.trim(),
          inputSchemaJson: inputSchemaJson || '{}',
          outputSchemaJson: outputSchemaJson || '{}',
          isDefault,
          isActive,
          version: version || '1.0.0',
          requiresApproval,
          riskLevel,
          executionLocation,
          implementationType: implementationType || undefined,
          implementationJson: implementationJson || undefined,
          documentationUrl: documentationUrl || undefined,
          exampleUsageJson: exampleUsageJson || undefined,
          metadataJson: metadataJson || undefined,
        });
        addNotification('Tool updated successfully', 'success');
        navigate(`/tools/${id}`);
      } else {
        const created = await toolService.createTool({
          name: name.trim(),
          displayName: displayName.trim(),
          description: description.trim(),
          category: category.trim(),
          inputSchemaJson: inputSchemaJson || '{}',
          outputSchemaJson: outputSchemaJson || '{}',
          isDefault,
          isActive,
          isSystem: false,
          version: version || '1.0.0',
          requiresApproval,
          riskLevel,
          executionLocation,
          implementationType: implementationType || undefined,
          implementationJson: implementationJson || undefined,
          documentationUrl: documentationUrl || undefined,
          exampleUsageJson: exampleUsageJson || undefined,
          metadataJson: metadataJson || undefined,
        });
        addNotification('Tool created successfully', 'success');
        navigate(`/tools/${created.id}`);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save tool';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 6 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate(isEdit && id ? `/tools/${id}` : '/tools')} sx={{ mr: -1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
              {title}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
              type="submit"
              form="tool-form"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form id="tool-form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Basic Information
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 4 }}>
                  {!isEdit && (
                    <TextField
                      label="Name"
                      value={name}
                      onChange={(e) => {
                        setNameTouched(true);
                        setName(e.target.value);
                      }}
                      fullWidth
                    />
                  )}
                  <TextField
                    label="Display Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Version"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    fullWidth
                  />
                </Box>

                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  sx={{ mb: 4 }}
                />

                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Risk Level</InputLabel>
                    <Select value={riskLevel} label="Risk Level" onChange={(e) => setRiskLevel(e.target.value)}>
                      <MenuItem value="Low">Low</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                      <MenuItem value="Critical">Critical</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Runs on</InputLabel>
                    <Select
                      value={executionLocation}
                      label="Runs on"
                      onChange={(e) => setExecutionLocation(Number(e.target.value) as ToolExecutionLocation)}
                    >
                      <MenuItem value={ToolExecutionLocation.Server}>Server Only</MenuItem>
                      <MenuItem value={ToolExecutionLocation.Client}>Client Only</MenuItem>
                      <MenuItem value={ToolExecutionLocation.Both}>Both</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={<Checkbox checked={requiresApproval} onChange={(e) => setRequiresApproval(e.target.checked)} />}
                    label="Requires approval"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
                    label="Default tool"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                    label="Active"
                  />
                </Box>
              </Box>

              <Divider />

              {/* Schemas */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Schemas
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Provide JSON Schemas for input and output validation.
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                  Input Schema (JSON)
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <CodeEditor value={inputSchemaJson} onChange={setInputSchemaJson} language="json" height="220px" />
                </Box>

                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                  Output Schema (JSON)
                </Typography>
                <Box>
                  <CodeEditor value={outputSchemaJson} onChange={setOutputSchemaJson} language="json" height="220px" />
                </Box>
              </Box>

              <Divider />

              {/* Implementation */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Implementation
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Implementation Type</InputLabel>
                    <Select value={implementationType} label="Implementation Type" onChange={(e) => setImplementationType(e.target.value)}>
                      <MenuItem value="CSharp">CSharp</MenuItem>
                      <MenuItem value="REST">REST</MenuItem>
                      <MenuItem value="RPA">RPA</MenuItem>
                      <MenuItem value="MCP">MCP</MenuItem>
                      <MenuItem value="Shell">Shell (server host)</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Documentation URL"
                    value={documentationUrl}
                    onChange={(e) => setDocumentationUrl(e.target.value)}
                    fullWidth
                  />
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
                    <strong>Local machine (user / developer PC):</strong> set <strong>Runs on: Client Only</strong> or <strong>Both</strong>{' '}
                    and do <strong>not</strong> use <strong>Shell</strong> as the implementation type. Keep something like{' '}
                    <strong>CSharp</strong> for this catalog row; run the real command in your <strong>AgentOps.Client</strong> app by
                    registering a tool with the <strong>same name and JSON schemas</strong> (for example <code>AgentOps.Client.Tools.RunShellTool</code>).
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>API server:</strong> pick <strong>Shell</strong> only if the process must run on the host where the API runs.
                    Then enable <strong>ShellExecution</strong> in API appsettings and use <strong>Runs on: Server Only</strong> unless you
                    deliberately use hybrid routing. For attach-system-tool, use <strong>Default tool</strong> (or system via API).
                  </Typography>
                </Alert>

                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                  Implementation Details (JSON)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {implementationType === 'Shell'
                    ? 'Not used for Shell — leave as {}. Command parameters come from the input JSON schema.'
                    : 'Optional configuration for REST, RPA, MCP, etc.'}
                </Typography>
                <Box>
                  <CodeEditor value={implementationJson} onChange={setImplementationJson} language="json" height="200px" />
                </Box>
              </Box>

              <Divider />

              {/* Metadata */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Metadata
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                  Example Usage (JSON)
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <CodeEditor value={exampleUsageJson} onChange={setExampleUsageJson} language="json" height="160px" />
                </Box>

                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                  Metadata (JSON)
                </Typography>
                <Box>
                  <CodeEditor value={metadataJson} onChange={setMetadataJson} language="json" height="160px" />
                </Box>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

